import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { createRef, useRef } from 'react';
import type { ButtonGridProps, ButtonGridRef } from './ButtonGrid.data';
import styles from './ButtonGrid.module.scss';
import { setupTransitionInTimeline } from './ButtonGrid.transitions';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import ButtonGridItem from './button-grid-item/ButtonGridItem';
import type { ButtonGridItemRef } from './button-grid-item/ButtonGridItem.data';
import type { HeadingRef } from 'components/atoms/heading/Heading.data';
import { getLink } from '../../../util/url.utils';
import { useRouter } from 'next/router';

export default function ButtonGrid({
  disableTriggerInOnScroll,
  canTransitionIn,
  className,
  blok,
  ref,
}: ButtonGridProps & { ref?: Ref<ButtonGridRef> }): ReactElement {
  const router = useRouter();
  const filteredItems = blok.text_items.filter((item) =>
    getLink(item.button[0]?.link, router.locale),
  );

  const itemsCount = filteredItems.length;
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HeadingRef>(null);
  const itemsRef = useRef(filteredItems.map(() => createRef<ButtonGridItemRef>()));

  const backgroundRef = useRef<HTMLDivElement>(null);

  useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        itemsRef,
        titleRef,
        backgroundRef,
      },
      setupTransitionInTimeline,
    }),
  });

  if (itemsCount === 0) return <></>;

  return (
    <div
      ref={elementRef}
      className={classNames(styles.buttonGrid, className)}
      {...sbEditable(blok)}
    >
      <div ref={backgroundRef} className={classNames(styles.background)} />
      {blok.title && (
        <Heading
          ref={titleRef}
          as="h4"
          size="label-large"
          className={classNames(styles.title)}
          transitionInType="slideUpFadeIn"
        >
          {blok.title}
        </Heading>
      )}

      <div
        className={classNames(styles.flexContainer, styles.threeColumn, {
          [styles.fourColumn]:
            itemsCount % 4 === 0 || (itemsCount % 3 !== 0 && itemsCount % 3 !== 2),
        })}
      >
        {filteredItems.map((content, index) => (
          <ButtonGridItem ref={itemsRef.current[index]} blok={content} key={content._uid} />
        ))}
      </div>
    </div>
  );
}
